import React, {useState, useEffect, useRef} from 'react';
import {Modal, Form, Input, InputNumber, Select, DatePicker, message, Row, Col, Card, Space, Button, List, Divider, Badge, Tag} from "antd";
import {MinusOutlined, PlusOutlined, ShoppingCartOutlined} from "@ant-design/icons";
import {Radio} from "antd";
import {getOrderById, updateOrder} from "../../../lib/api_cw_orders";
import {getStocks} from "../../../lib/api_stock";
import moment from "moment";

const OrderEditPosModal = ({id, onCancel, onOk}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedStore, setSelectedStore] = useState('门店1');
    const [stocks, setStocks] = useState([]);
    const [cartItems, setCartItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const lastRequestDateRef = useRef('');

    // 门店选项
    const storeOptions = [
        { label: '门店1', value: '门店1' },
        { label: '门店2', value: '门店2' }
    ];

    // 获取订单详情和初始化数据
    const fetchData = () => {
        // 获取订单详情
        getOrderById(id)
            .then(res => {
                if (res.code === 0) {
                    const order = res.data;
                    
                    // 设置表单初始值
                    form.setFieldsValue({
                        order_no: order.order_no,
                        order_date: order.order_date ? moment(order.order_date) : moment(),
                        md: order.md || '门店1',
                        customer_name: order.customer_name,
                        customer_phone: order.customer_phone,
                        remark: order.remark
                    });
                    
                    setSelectedStore(order.md || '门店1');
                    setDiscountAmount(parseFloat(order.discount_amount) || 0);
                    
                    // 如果有订单商品，转换为购物车格式
                    if (order.order_items && order.order_items.length > 0) {
                        const items = order.order_items.map(item => ({
                            id: item.stock_id,
                            name: item.stock_name,
                            price: parseFloat(item.unit_price),
                            quantity: item.quantity,
                            selectedPrice: parseFloat(item.unit_price),
                            discountAmount: parseFloat(item.discount_amount) || 0
                        }));
                        setCartItems(items);
                    }
                } else {
                    message.error(res.message || '获取订单详情失败');
                    onCancel && onCancel();
                }
            })
            .catch(() => {
                message.error('获取订单详情失败');
                onCancel && onCancel();
            });


    };

    useEffect(fetchData, [id]);

    // 获取商品列表
    useEffect(() => {
        if (selectedStore) {
            // 根据门店名称获取对应的商品列表
            // 这里假设所有门店共享相同的商品列表
            getStocks(1, 100) // 获取前100个商品
                .then(res => {
                    if (res.code === 0) {
                        setStocks(res.data.list || []);
                    }
                })
                .catch(() => {
                    message.error('获取商品列表失败');
                });
        }
    }, [selectedStore]);

    // 添加商品到购物车
    const addToCart = (stock) => {
        const existingItem = cartItems.find(item => item.id === stock.id);
        
        if (existingItem) {
            setCartItems(cartItems.map(item => 
                item.id === stock.id 
                    ? {...item, quantity: item.quantity + 1}
                    : item
            ));
        } else {
            setCartItems([...cartItems, {
                ...stock,
                quantity: 1,
                selectedPrice: stock.price,
                discountAmount: 0
            }]);
        }
    };

    // 更新购物车商品数量
    const updateCartItemQuantity = (itemId, newQuantity) => {
        if (newQuantity < 1) {
            // 如果数量为0，从购物车中移除
            setCartItems(cartItems.filter(item => item.id !== itemId));
        } else {
            setCartItems(cartItems.map(item => 
                item.id === itemId 
                    ? {...item, quantity: newQuantity}
                    : item
            ));
        }
    };

    // 从购物车中移除商品
    const removeFromCart = (itemId) => {
        setCartItems(cartItems.filter(item => item.id !== itemId));
    };

    // 计算总价
    const calculateTotal = () => {
        return cartItems.reduce((total, item) => {
            const itemTotal = (item.selectedPrice * item.quantity) - (item.discountAmount || 0);
            return total + Math.max(0, itemTotal);
        }, 0);
    };

    // 计算原价
    const calculateOriginalTotal = () => {
        return cartItems.reduce((total, item) => {
            return total + (item.price * item.quantity);
        }, 0);
    };

    // 计算优惠金额分摊
    const calculateDiscountDistribution = () => {
        if (cartItems.length === 0 || discountAmount <= 0) {
            return cartItems;
        }

        const originalTotal = calculateOriginalTotal();
        if (originalTotal <= 0) return cartItems;

        let remainingDiscount = discountAmount;
        const updatedItems = cartItems.map((item, index) => {
            const itemOriginalTotal = item.price * item.quantity;
            const itemDiscountRatio = itemOriginalTotal / originalTotal;
            
            if (index === cartItems.length - 1) {
                return {
                    ...item,
                    discountAmount: remainingDiscount
                };
            }
            
            const itemDiscount = Math.round(itemDiscountRatio * discountAmount * 100) / 100;
            remainingDiscount -= itemDiscount;
            
            return {
                ...item,
                discountAmount: itemDiscount
            };
        });

        return updatedItems;
    };

    // 更新购物车商品优惠金额
    useEffect(() => {
        if (cartItems.length > 0 && discountAmount > 0) {
            const updatedItems = calculateDiscountDistribution();
            setCartItems(updatedItems);
        }
    }, [discountAmount, cartItems.length]);

    // 过滤商品列表
    const filteredStocks = stocks.filter(stock => {
        return stock.name.toLowerCase().includes(searchText.toLowerCase()) || 
               (stock.category && stock.category.toLowerCase().includes(searchText.toLowerCase()));
    });

    const handleSubmit = () => {
        form.validateFields()
            .then(values => {
                if (cartItems.length === 0) {
                    message.error('请至少添加一个商品');
                    return;
                }

                setLoading(true);
                
                // 构建订单数据
                const orderData = {
                    ...values,
                    order_date: values.order_date ? values.order_date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    original_amount: calculateOriginalTotal(),
                    discount_amount: discountAmount,
                    total_amount: calculateTotal(),
                    actual_amount: calculateTotal(),
                    order_status: 2,
                    order_items: cartItems.map(item => ({
                        stock_id: item.id,
                        stock_name: item.name,
                        unit_price: item.selectedPrice,
                        quantity: item.quantity,
                        subtotal: item.selectedPrice * item.quantity,
                        discount_amount: item.discountAmount || 0,
                        actual_amount: (item.selectedPrice * item.quantity) - (item.discountAmount || 0)
                    }))
                };

                updateOrder(id, orderData)
                    .then(res => {
                        if (res.code === 0) {
                            message.success('更新订单成功');
                            onOk && onOk();
                        } else {
                            message.error(res.message || '更新订单失败');
                        }
                    })
                    .catch(() => {
                        message.error('更新订单失败');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            })
            .catch(info => {
                console.log('Validate Failed:', info);
            });
    };

    const handleCancel = () => {
        onCancel && onCancel();
    };

    return (
        <Modal
            title="编辑订单"
            visible={true}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnClose={true}
            width={1000}
            style={{ top: 20 }}
            bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        >
            <Row gutter={16}>
                {/* 左侧：商品列表 */}
                <Col span={12}>
                    <Card 
                        title="商品列表" 
                        extra={
                            <Input.Search
                                placeholder="搜索商品名称或分类"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{width: 200}}
                            />
                        }
                        style={{height: '500px', overflow: 'auto'}}
                    >
                        <Row gutter={[16, 16]}>
                            {filteredStocks.map(stock => (
                                <Col span={8} key={stock.id}>
                                    <Card
                                        hoverable
                                        onClick={() => addToCart(stock)}
                                        bodyStyle={{padding: '16px', textAlign: 'center'}}
                                    >
                                        <div style={{fontSize: '16px', fontWeight: 'bold', marginBottom: '8px'}}>
                                            {stock.name}
                                        </div>
                                        <div style={{fontSize: '18px', color: '#1890ff', fontWeight: 'bold'}}>
                                            ¥{stock.price}
                                        </div>
                                        {stock.quantity > 0 && (
                                            <Tag color="green" style={{marginTop: '8px'}}>库存: {stock.quantity}</Tag>
                                        )}
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>

                {/* 右侧：购物车和订单信息 */}
                <Col span={12}>
                    <div style={{display: 'flex', flexDirection: 'column', height: '500px'}}>
                        {/* 购物车 */}
                        <Card 
                            title={
                                <Space>
                                    <ShoppingCartOutlined />
                                    购物车
                                    <Badge count={cartItems.reduce((total, item) => total + item.quantity, 0)} />
                                </Space>
                            }
                            style={{flex: 1, marginBottom: '16px', display: 'flex', flexDirection: 'column'}}
                        >
                            {/* 购物车商品列表 */}
                            <div style={{flex: 1, overflow: 'auto', marginBottom: '16px'}}>
                                <List
                                    dataSource={cartItems}
                                    renderItem={(item) => (
                                        <List.Item
                                            actions={[
                                                <Button 
                                                    type="link" 
                                                    danger 
                                                    size="small"
                                                    onClick={() => removeFromCart(item.id)}
                                                >
                                                    删除
                                                </Button>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                title={item.name}
                                                description={
                                                    <Space direction="vertical" size="small" style={{width: '100%'}}>
                                                        <Space>
                                                            <Button 
                                                                size="small" 
                                                                icon={<MinusOutlined />}
                                                                onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                                                            />
                                                            <InputNumber 
                                                                size="small" 
                                                                min={1}
                                                                value={item.quantity}
                                                                style={{width: '60px'}}
                                                                onChange={(value) => updateCartItemQuantity(item.id, value)}
                                                            />
                                                            <Button 
                                                                size="small" 
                                                                icon={<PlusOutlined />}
                                                                onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                                                            />
                                                            <span>¥{item.selectedPrice}</span>
                                                            <span>× {item.quantity}</span>
                                                            <span>= ¥{(item.selectedPrice * item.quantity).toFixed(2)}</span>
                                                        </Space>
                                                        {item.discountAmount > 0 && (
                                                            <Space style={{color: '#ff4d4f', fontSize: '12px'}}>
                                                                <span>优惠: -¥{item.discountAmount.toFixed(2)}</span>
                                                                <span>实付: ¥{((item.selectedPrice * item.quantity) - item.discountAmount).toFixed(2)}</span>
                                                            </Space>
                                                        )}
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            </div>

                            {/* 价格汇总 */}
                            <div style={{borderTop: '1px solid #f0f0f0', paddingTop: '16px'}}>
                                <Space direction="vertical" style={{width: '100%'}}>
                                    <Row justify="space-between">
                                        <Col>原价:</Col>
                                        <Col>¥{calculateOriginalTotal().toFixed(2)}</Col>
                                    </Row>
                                    <Row justify="space-between" align="middle">
                                        <Col>优惠:</Col>
                                        <Col>
                                            <InputNumber 
                                                size="small"
                                                min={0}
                                                max={calculateOriginalTotal()}
                                                value={discountAmount}
                                                onChange={setDiscountAmount}
                                                style={{width: '100px'}}
                                                prefix="-¥"
                                                precision={2}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row justify="space-between" style={{fontSize: '16px', fontWeight: 'bold'}}>
                                        <Col>总计:</Col>
                                        <Col>¥{calculateTotal().toFixed(2)}</Col>
                                    </Row>
                                </Space>
                            </div>
                        </Card>

                        {/* 订单基本信息 */}
                        <Card style={{flexShrink: 0}}>
                        <Form
                            form={form}
                            layout="vertical"
                            size="small"
                        >
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item
                                        label="订单号"
                                        name="order_no"
                                    >
                                        <Input placeholder="订单号" readOnly />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="订单日期"
                                        name="order_date"
                                    >
                                        <DatePicker 
                                            style={{width: '100%'}} 
                                            format="YYYY-MM-DD" 
                                            disabled
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item
                                        label="门店"
                                        name="md"
                                    >
                                        <Radio.Group 
                                            options={storeOptions}
                                            value={selectedStore}
                                            optionType="button"
                                            buttonStyle="solid"
                                            disabled
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="客户姓名"
                                        name="customer_name"
                                    >
                                        <Input placeholder="请输入客户姓名" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item
                                        label="客户电话"
                                        name="customer_phone"
                                    >
                                        <Input placeholder="请输入客户电话" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="备注"
                                        name="remark"
                                    >
                                        <Input.TextArea rows={2} placeholder="请输入备注信息" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                        </Card>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default OrderEditPosModal;